(() => {
  const dialog = document.querySelector('[data-quick-add-dialog]');
  if (!dialog || dialog.dataset.quickAddInitialized === 'true') return;

  dialog.dataset.quickAddInitialized = 'true';

  const panel = dialog.querySelector('[data-quick-add-panel]');
  const loading = dialog.querySelector('[data-quick-add-loading]');
  const error = dialog.querySelector('[data-quick-add-error]');
  const content = dialog.querySelector('[data-quick-add-content]');
  const title = dialog.querySelector('[data-quick-add-title]');
  const price = dialog.querySelector('[data-quick-add-price]');
  const comparePrice = dialog.querySelector('[data-quick-add-compare-price]');
  const badge = dialog.querySelector('[data-quick-add-badge]');
  const thumbnails = dialog.querySelector('[data-quick-add-thumbnails]');
  const thumbnailRail = thumbnails?.closest('.quick-add-modal__thumbnail-rail');
  const thumbnailScrollbar = dialog.querySelector('[data-quick-add-thumbnail-scrollbar]');
  const thumbnailScrollbarThumb = dialog.querySelector('[data-quick-add-thumbnail-scrollbar-thumb]');
  const mainMedia = dialog.querySelector('[data-quick-add-main-media]');
  const mainImageViewport = dialog.querySelector('[data-quick-add-main-image-viewport]') || mainMedia;
  const imagePlaceholder = dialog.querySelector('[data-quick-add-image-placeholder]');
  const optionsContainer = dialog.querySelector('[data-quick-add-options]');
  const form = dialog.querySelector('[data-quick-add-form]');
  const variantInput = dialog.querySelector('[data-quick-add-variant-id]');
  const quantityInput = dialog.querySelector('[data-quick-add-quantity]');
  const ruleNotice = dialog.querySelector('[data-quick-add-notice]');
  const minusButton = dialog.querySelector('[data-quick-add-minus]');
  const plusButton = dialog.querySelector('[data-quick-add-plus]');
  const submitButton = dialog.querySelector('[data-quick-add-submit]');
  const submitLabel = dialog.querySelector('[data-quick-add-submit-label]');
  const purchaseRow = dialog.querySelector('.quick-add-modal__purchase');
  const quantityWrapper = dialog.querySelector('.quick-add-modal__quantity');
  const soldOutSlot = dialog.querySelector('[data-quick-add-sold-out-app]');
  const productDataElement = dialog.querySelector('[data-product-data]');
  const closeButton = dialog.querySelector('[data-quick-add-close]');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktopGallery = window.matchMedia('(min-width: 750px)');
  const mobileGallery = window.matchMedia('(max-width: 749px)');
  const productCache = new Map();

  let activeProduct = null;
  let activeVariant = null;
  let selectedOptions = [];
  let activeImages = [];
  let activeImageUrl = '';
  let activeProductUrl = '';
  let lastTrigger = null;
  let requestVersion = 0;
  let imageRequestVersion = 0;
  let closeTimer = 0;
  let prefetchTimer = 0;
  let thumbnailScrollFrame = 0;
  let thumbnailWheelFrame = 0;
  let thumbnailWheelDelta = 0;
  let thumbnailPanFrame = 0;
  let thumbnailPanPointer = null;
  let thumbnailPanStartY = 0;
  let thumbnailPanStartScroll = 0;
  let thumbnailPanTargetScroll = 0;
  let thumbnailPanMoved = false;
  let suppressThumbnailClick = false;
  let thumbnailDragPointer = null;
  let thumbnailDragOffset = 0;
  let thumbnailResizeObserver = null;
  let mainSlides = [];
  let mainViewportScrollFrame = 0;
  let mainViewportTargetIndex = null;
  let mainViewportTargetTimer = 0;

  const formatWithDelimiters = (value, precision = 2, thousands = ',', decimal = '.') => {
    if (!Number.isFinite(value)) return '0';

    const parts = (value / 100).toFixed(precision).split('.');
    parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    return parts[1] ? `${parts[0]}${decimal}${parts[1]}` : parts[0];
  };

  const formatMoney = (cents, format = '${{amount}}') => {
    const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/)?.[1] || 'amount';
    const formats = {
      amount: [2, ',', '.'],
      amount_no_decimals: [0, ',', '.'],
      amount_with_comma_separator: [2, '.', ','],
      amount_no_decimals_with_comma_separator: [0, '.', ','],
      amount_with_space_separator: [2, ' ', ','],
      amount_no_decimals_with_space_separator: [0, ' ', ','],
      amount_with_apostrophe_separator: [2, "'", '.'],
    };
    const [precision, thousands, decimal] = formats[placeholder] || formats.amount;

    return format.replace(
      /\{\{\s*\w+\s*\}\}/,
      formatWithDelimiters(Number(cents), precision, thousands, decimal)
    );
  };

  const imageSource = (image) => {
    if (!image) return '';
    if (typeof image === 'string') return image;
    return image.src
      || image.url
      || image.preview_image?.src
      || image.preview_image?.url
      || '';
  };

  const imageKey = (source) => {
    if (!source) return '';
    try {
      const parsed = new URL(source, window.location.origin);
      return `${parsed.origin}${parsed.pathname}`;
    } catch (loadError) {
      return source.split('?')[0];
    }
  };

  const imageWithWidth = (source, width) => {
    if (!source) return '';
    try {
      const parsed = new URL(source, window.location.origin);
      parsed.searchParams.set('width', String(width));
      return parsed.toString();
    } catch (loadError) {
      return source;
    }
  };

  const applyResponsiveImageSource = (image, source) => {
    image.srcset = [240, 360, 480, 720, 900]
      .map((width) => `${imageWithWidth(source, width)} ${width}w`)
      .join(', ');
    image.sizes = '(min-width: 1110px) 403px, (min-width: 750px) 36vw, 164px';
    image.src = imageWithWidth(source, 900);
  };

  const variantImage = (variant) => imageSource(
    variant?.featured_image
      || variant?.featured_media?.preview_image
      || variant?.featured_media
  );

  const normalizeVariants = (variants) => (variants || []).map((variant) => ({
    ...variant,
    id: Number(variant.id),
    available: Boolean(variant.available),
    options: Array.isArray(variant.options)
      ? variant.options.map(String)
      : [variant.option1, variant.option2, variant.option3].filter((value) => value != null).map(String),
  }));

  const normalizeOptions = (rawOptions, variants) => {
    const maximumOptionCount = variants.reduce((maximum, variant) => Math.max(maximum, variant.options.length), 0);
    const sourceOptions = Array.isArray(rawOptions) && rawOptions.length
      ? rawOptions
      : Array.from({ length: maximumOptionCount }, (_, index) => ({ name: `Option ${index + 1}` }));

    return sourceOptions.map((option, index) => {
      const name = typeof option === 'string' ? option : option?.name || `Option ${index + 1}`;
      const suppliedValues = typeof option === 'object' && Array.isArray(option?.values) ? option.values : [];
      const derivedValues = variants.map((variant) => variant.options[index]).filter(Boolean);
      return {
        index,
        name,
        values: [...new Set((suppliedValues.length ? suppliedValues : derivedValues).map(String))],
      };
    });
  };

  const normalizeProduct = (rawProduct, productUrl) => {
    const variants = normalizeVariants(rawProduct.variants);
    const optionDefinitions = normalizeOptions(rawProduct.options, variants);
    const imageCandidates = [
      ...(rawProduct.images || []).map(imageSource),
      imageSource(rawProduct.featured_image),
      ...variants.map(variantImage),
    ].filter(Boolean);
    const seenImages = new Set();
    const images = imageCandidates.filter((source) => {
      const key = imageKey(source);
      if (!key || seenImages.has(key)) return false;
      seenImages.add(key);
      return true;
    });

    return {
      ...rawProduct,
      title: rawProduct.title || '',
      vendor: rawProduct.vendor || '',
      url: rawProduct.url || productUrl,
      variants,
      optionDefinitions,
      images,
    };
  };

  const productJsonUrl = (productUrl) => {
    const parsed = new URL(productUrl, window.location.origin);
    parsed.hash = '';
    parsed.search = '';
    parsed.pathname = `${parsed.pathname.replace(/\/$/, '').replace(/\.js$/, '')}.js`;
    return parsed.toString();
  };

  const fetchProduct = (productUrl) => {
    const cacheKey = productJsonUrl(productUrl);
    if (productCache.has(cacheKey)) return productCache.get(cacheKey);

    const request = fetch(cacheKey, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Quick add request failed: ${response.status}`);
        return response.json();
      })
      .then((product) => normalizeProduct(product, productUrl))
      .catch((loadError) => {
        productCache.delete(cacheKey);
        throw loadError;
      });

    productCache.set(cacheKey, request);
    return request;
  };

  const isDefaultOption = (definition) => (
    activeProduct?.variants.length === 1
    && definition.index === 0
    && definition.values.length === 1
    && definition.values[0].toLowerCase() === 'default title'
  );

  const visibleOptionDefinitions = () => (
    activeProduct?.optionDefinitions.filter((definition) => !isDefaultOption(definition)) || []
  );

  const renderTitle = (productTitle) => {
    title.replaceChildren();
    const parts = productTitle.split(' - ');
    if (parts.length < 2) {
      title.textContent = productTitle;
      return;
    }

    title.append(document.createTextNode(`${parts.shift().trim()} - `));
    const accent = document.createElement('span');
    accent.className = 'quick-add-modal__title-accent';
    accent.textContent = parts.join(' - ').trim();
    title.append(accent);
  };

  const mainImageIndex = (source) => activeImages.findIndex(
    (candidate) => imageKey(candidate) === imageKey(source)
  );

  const updateMainImageIndicators = () => {
    thumbnails.querySelectorAll('[data-quick-add-thumbnail]').forEach((thumbnail) => {
      const isActive = imageKey(thumbnail.dataset.imageUrl) === imageKey(activeImageUrl);
      thumbnail.classList.toggle('is-active', isActive);
      thumbnail.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  };

  const hydrateMainSlide = (index) => {
    const slide = mainSlides[index];
    if (!slide || slide.dataset.imageLoaded === 'true') return;
    applyResponsiveImageSource(slide, slide.dataset.imageUrl);
    slide.dataset.imageLoaded = 'true';
  };

  const hydrateNearbyMainSlides = (index) => {
    hydrateMainSlide(index);
    if (!mobileGallery.matches) return;
    hydrateMainSlide(index - 1);
    hydrateMainSlide(index + 1);
  };

  const renderMainSlides = () => {
    mainImageViewport.replaceChildren();
    mainSlides = activeImages.map((source) => {
      const image = document.createElement('img');
      image.className = 'quick-add-modal__main-image';
      image.dataset.quickAddMainSlide = '';
      image.dataset.imageUrl = source;
      image.width = 800;
      image.height = 800;
      image.alt = activeProduct?.title || '';
      image.loading = 'lazy';
      image.decoding = 'async';
      image.draggable = false;
      image.setAttribute('aria-hidden', 'true');
      mainImageViewport.append(image);
      return image;
    });
  };

  const activateMainSlide = (index, { showLoading = false } = {}) => {
    if (!mainSlides.length) {
      activeImageUrl = '';
      imagePlaceholder.hidden = false;
      mainMedia.classList.remove('is-loading');
      updateMainImageIndicators();
      return;
    }

    const safeIndex = Math.min(Math.max(index, 0), mainSlides.length - 1);
    const slide = mainSlides[safeIndex];
    const requestId = ++imageRequestVersion;
    activeImageUrl = slide.dataset.imageUrl;
    slide.loading = 'eager';
    hydrateNearbyMainSlides(safeIndex);

    mainSlides.forEach((candidate, candidateIndex) => {
      const isActive = candidateIndex === safeIndex;
      candidate.classList.toggle('is-active', isActive);
      candidate.setAttribute('aria-hidden', String(!isActive));
    });
    updateMainImageIndicators();
    imagePlaceholder.hidden = true;

    const isLoaded = slide.complete && slide.naturalWidth > 0;
    mainMedia.classList.toggle('is-loading', showLoading && !isLoaded);
    if (isLoaded || !showLoading) return;

    const finish = () => {
      slide.removeEventListener('load', finish);
      slide.removeEventListener('error', finish);
      if (requestId === imageRequestVersion) mainMedia.classList.remove('is-loading');
    };
    slide.addEventListener('load', finish);
    slide.addEventListener('error', finish);
  };

  const scrollMainViewportTo = (index, behavior) => {
    if (!mobileGallery.matches || !mainSlides[index] || mainImageViewport.clientWidth <= 0) return;
    mainViewportTargetIndex = index;
    window.clearTimeout(mainViewportTargetTimer);
    mainViewportTargetTimer = window.setTimeout(() => {
      mainViewportTargetIndex = null;
    }, behavior === 'smooth' ? 700 : 0);
    mainImageViewport.scrollTo({ left: mainSlides[index].offsetLeft, behavior });
  };

  const setMainImage = (source, { skipLoading = false } = {}) => {
    const targetIndex = mainImageIndex(source);
    if (targetIndex < 0) {
      activateMainSlide(0, { showLoading: !skipLoading });
      return;
    }

    const currentIndex = mainImageIndex(activeImageUrl);
    const shouldAnimate = (
      mobileGallery.matches
      && !reducedMotion.matches
      && currentIndex >= 0
      && Math.abs(targetIndex - currentIndex) === 1
    );
    activateMainSlide(targetIndex, { showLoading: !skipLoading });
    scrollMainViewportTo(targetIndex, shouldAnimate ? 'smooth' : 'auto');
  };

  const syncMainViewportPosition = () => {
    const activeIndex = mainImageIndex(activeImageUrl);
    if (activeIndex < 0) return;
    scrollMainViewportTo(activeIndex, 'auto');
  };

  const updateMainImageFromScroll = () => {
    mainViewportScrollFrame = 0;
    if (!mobileGallery.matches || mainViewportTargetIndex !== null || !mainSlides.length) return;

    const scrollLeft = mainImageViewport.scrollLeft;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;
    mainSlides.forEach((slide, index) => {
      const distance = Math.abs(slide.offsetLeft - scrollLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });
    if (mainImageIndex(activeImageUrl) !== closestIndex) activateMainSlide(closestIndex);
  };

  const scheduleMainImageFromScroll = () => {
    if (mainViewportScrollFrame) return;
    mainViewportScrollFrame = window.requestAnimationFrame(updateMainImageFromScroll);
  };

  const updateThumbnailScrollbar = () => {
    if (!thumbnailScrollbar || !thumbnailScrollbarThumb) return;

    const maximumScroll = Math.max(0, thumbnails.scrollHeight - thumbnails.clientHeight);
    const shouldShow = desktopGallery.matches && maximumScroll > 1;
    thumbnailScrollbar.hidden = !shouldShow;
    thumbnailRail?.classList.toggle('is-scrollable', shouldShow);

    if (!shouldShow) {
      thumbnailScrollbar.style.setProperty('--quick-add-thumbnail-thumb-offset', '0px');
      return;
    }

    const maximumThumbOffset = Math.max(
      0,
      thumbnailScrollbar.clientHeight - thumbnailScrollbarThumb.getBoundingClientRect().height
    );
    const progress = Math.min(1, Math.max(0, thumbnails.scrollTop / maximumScroll));
    thumbnailScrollbar.style.setProperty(
      '--quick-add-thumbnail-thumb-offset',
      `${progress * maximumThumbOffset}px`
    );
  };

  const scheduleThumbnailScrollbar = () => {
    if (thumbnailScrollFrame) return;
    thumbnailScrollFrame = window.requestAnimationFrame(() => {
      thumbnailScrollFrame = 0;
      updateThumbnailScrollbar();
    });
  };

  const queueThumbnailWheel = (event) => {
    if (!desktopGallery.matches) return;

    const maximumScroll = Math.max(0, thumbnails.scrollHeight - thumbnails.clientHeight);
    if (maximumScroll <= 0) return;

    const rawDelta = Math.abs(event.deltaY) >= Math.abs(event.deltaX)
      ? event.deltaY
      : event.deltaX;
    if (!rawDelta) return;

    event.preventDefault();
    const deltaMultiplier = event.deltaMode === 1
      ? 16
      : event.deltaMode === 2
        ? thumbnails.clientHeight
        : 1;
    thumbnailWheelDelta += rawDelta * deltaMultiplier;
    if (thumbnailWheelFrame) return;

    thumbnailWheelFrame = window.requestAnimationFrame(() => {
      thumbnailWheelFrame = 0;
      const nextMaximumScroll = Math.max(0, thumbnails.scrollHeight - thumbnails.clientHeight);
      thumbnails.scrollTop = Math.min(
        nextMaximumScroll,
        Math.max(0, thumbnails.scrollTop + thumbnailWheelDelta)
      );
      thumbnailWheelDelta = 0;
      scheduleThumbnailScrollbar();
    });
  };

  const queueThumbnailPan = () => {
    if (thumbnailPanFrame) return;
    thumbnailPanFrame = window.requestAnimationFrame(() => {
      thumbnailPanFrame = 0;
      const maximumScroll = Math.max(0, thumbnails.scrollHeight - thumbnails.clientHeight);
      thumbnails.scrollTop = Math.min(maximumScroll, Math.max(0, thumbnailPanTargetScroll));
      scheduleThumbnailScrollbar();
    });
  };

  const finishThumbnailPan = (event) => {
    if (thumbnailPanPointer !== event.pointerId) return;

    if (thumbnails.hasPointerCapture(event.pointerId)) {
      thumbnails.releasePointerCapture(event.pointerId);
    }
    thumbnailPanPointer = null;
    thumbnails.classList.remove('is-dragging');

    if (thumbnailPanMoved) {
      suppressThumbnailClick = true;
      window.setTimeout(() => {
        suppressThumbnailClick = false;
      }, 0);
    }
    thumbnailPanMoved = false;
  };

  const scrollThumbnailsFromPointer = (clientY) => {
    if (!thumbnailScrollbar || !thumbnailScrollbarThumb || thumbnailScrollbar.hidden) return;

    const trackRect = thumbnailScrollbar.getBoundingClientRect();
    const thumbHeight = thumbnailScrollbarThumb.getBoundingClientRect().height;
    const maximumThumbOffset = Math.max(0, trackRect.height - thumbHeight);
    const maximumScroll = Math.max(0, thumbnails.scrollHeight - thumbnails.clientHeight);
    if (maximumThumbOffset <= 0 || maximumScroll <= 0) return;

    const thumbOffset = Math.min(
      maximumThumbOffset,
      Math.max(0, clientY - trackRect.top - thumbnailDragOffset)
    );
    thumbnails.scrollTop = (thumbOffset / maximumThumbOffset) * maximumScroll;
  };

  const renderGallery = () => {
    renderMainSlides();
    mainImageViewport.scrollLeft = 0;
    thumbnails.replaceChildren();
    thumbnails.scrollTop = 0;
    thumbnails.scrollLeft = 0;
    const dotCount = Math.max(activeImages.length, 1);
    const dotRailWidth = Math.min(162, (dotCount * 7) + 15);
    thumbnailRail?.style.setProperty('--quick-add-dots-width', `${dotRailWidth}px`);

    activeImages.forEach((source, index) => {
      const button = document.createElement('button');
      button.className = 'quick-add-modal__thumbnail';
      button.type = 'button';
      button.dataset.quickAddThumbnail = '';
      button.dataset.imageUrl = source;
      button.setAttribute('aria-label', `Afficher l’image ${index + 1}`);

      const image = document.createElement('img');
      image.src = imageWithWidth(source, 160);
      image.alt = '';
      image.width = 160;
      image.height = 160;
      image.loading = desktopGallery.matches && index < 3 ? 'eager' : 'lazy';
      image.decoding = 'async';
      image.draggable = false;
      button.append(image);
      thumbnails.append(button);
    });
    scheduleThumbnailScrollbar();
  };

  const variantMatches = (variant, options) => (
    variant.options.length === options.length
    && variant.options.every((value, index) => value === options[index])
  );

  const findExactVariant = (options) => activeProduct?.variants.find((variant) => variantMatches(variant, options));

  const isFormatOption = (definition) => /^(format|size|poids|weight)$/i.test(definition?.name.trim() || '');

  // requireAvailable false answers "does this value exist at all", which is what
  // separates a sold-out value (selectable) from one with no variant behind it.
  const optionHasVariant = (optionIndex, optionValue, requireAvailable = true) => {
    // Format controls stay reachable even when the current flavour is sold out
    // in another size. Flavours must match the currently selected format/options.
    const canChangeOtherOptions = isFormatOption(activeProduct.optionDefinitions[optionIndex]);
    return activeProduct.variants.some((variant) => (
      (variant.available || !requireAvailable)
      && variant.options[optionIndex] === optionValue
      && (canChangeOtherOptions || variant.options.every((value, index) => (
        index === optionIndex || value === selectedOptions[index]
      )))
    ));
  };

  const bestVariantFor = (optionIndex, optionValue, requireAvailable = true) => {
    const candidates = activeProduct?.variants.filter(
      (variant) => (variant.available || !requireAvailable) && variant.options[optionIndex] === optionValue
    ) || [];

    return candidates.sort((left, right) => {
      const score = (variant) => variant.options.reduce(
        (total, value, index) => total + (index !== optionIndex && value === selectedOptions[index] ? 1 : 0),
        0
      );
      return score(right) - score(left);
    })[0];
  };

  const variantMaximum = (variant) => {
    const quantityRuleMaximum = Number(variant?.quantity_rule?.max);
    if (Number.isFinite(quantityRuleMaximum) && quantityRuleMaximum > 0) return quantityRuleMaximum;

    const inventoryQuantity = Number(variant?.inventory_quantity);
    if (
      variant?.inventory_management
      && variant?.inventory_policy === 'deny'
      && Number.isFinite(inventoryQuantity)
      && inventoryQuantity > 0
    ) {
      return inventoryQuantity;
    }

    return Number.POSITIVE_INFINITY;
  };

  const CART_RULE_ATTRIBUTES = ['data-cart-rules', 'data-max-quantity', 'data-incompatible-ids'];

  const copyCartRules = (source, target) => {
    CART_RULE_ATTRIBUTES.forEach((attribute) => {
      const value = source.getAttribute(attribute);
      if (value === null) target.removeAttribute(attribute);
      else target.setAttribute(attribute, value);
    });
  };

  const cartRuleVerdict = () => (
    form.hasAttribute('data-cart-rules') ? window.CartRules?.evaluate(form, 1) || null : null
  );

  // Points-only state for the currently-open product (set from the trigger's
  // data-* in openDialog; /product.js has no metafields, so the card passes it).
  let fpoPointsOnly = false;
  let fpoPointsCost = 0;
  const fpoPriceContainer = dialog.querySelector('.quick-add-modal__price');

  const updatePrices = () => {
    // Points-only product → show loyalty points (× quantity) instead of money.
    // We drop data-promo-price so the promo engine doesn't overwrite it, and
    // restore it for normal products since this modal is shared/reused.
    if (fpoPointsOnly && fpoPointsCost > 0) {
      if (fpoPriceContainer) fpoPriceContainer.removeAttribute('data-promo-price');
      comparePrice.textContent = '';
      comparePrice.hidden = true;
      const fpoLoggedIn = dialog.dataset.fpoLoggedIn === 'true';
      if (!fpoLoggedIn) {
        // Logged out → show a "connect to your account" link in the price box.
        const loginUrl = dialog.dataset.fpoLoginUrl || '/account/login';
        price.innerHTML = '<a class="quick-add-modal__price-login" href="' + loginUrl + '">Connectez-vous</a>';
      } else {
        const fpoQty = Math.max(1, parseInt(quantityInput?.value, 10) || 1);
        price.textContent = fpoPointsCost * fpoQty + ' points';
      }
      // Points-only can't be added for money from the modal — send the buyer to
      // the product page where the "buy with points" flow lives.
      if (submitLabel) submitLabel.textContent = 'Voir le produit';
      if (submitButton) submitButton.disabled = false;
      return;
    }
    if (fpoPriceContainer && !fpoPriceContainer.hasAttribute('data-promo-price')) {
      fpoPriceContainer.setAttribute('data-promo-price', '');
    }

    const priceAmount = Number(activeVariant?.price);
    const compareAmount = Number(activeVariant?.compare_at_price);
    const promoPrice = price.closest('[data-promo-price]');

    if (promoPrice && Number.isFinite(priceAmount)) {
      promoPrice.dataset.basePrice = String(priceAmount);
      promoPrice.dataset.comparePrice = Number.isFinite(compareAmount) ? String(compareAmount) : '0';
      promoPrice.dataset.promoQuantity = '1';
      if (window.FWNPromoPricing) {
        window.FWNPromoPricing.renderElement(promoPrice);
        return;
      }
    }

    price.textContent = Number.isFinite(priceAmount)
      ? formatMoney(priceAmount, dialog.dataset.moneyFormat)
      : '';

    const hasComparePrice = Number.isFinite(compareAmount) && compareAmount > priceAmount;
    comparePrice.textContent = hasComparePrice
      ? formatMoney(compareAmount, dialog.dataset.moneyFormat)
      : '';
    comparePrice.hidden = !hasComparePrice;
  };

  const clampQuantity = (difference = 0) => {
    const minimum = Number(quantityInput.min) || 1;
    const maximum = Number(quantityInput.max) || Number.POSITIVE_INFINITY;
    const current = Number.parseInt(quantityInput.value, 10) || minimum;
    const quantity = Math.min(Math.max(current + difference, minimum), maximum);
    quantityInput.value = String(quantity);
    minusButton.disabled = quantity <= minimum;
    plusButton.disabled = !activeVariant?.available || (Number.isFinite(maximum) && quantity >= maximum);
    updatePrices();
  };

  const updateOptionStates = () => {
    optionsContainer.querySelectorAll('[data-quick-add-option-value]').forEach((button) => {
      const optionIndex = Number(button.dataset.optionIndex);
      const optionValue = button.dataset.optionValue;
      const isSelected = selectedOptions[optionIndex] === optionValue;
      const hasVariant = optionHasVariant(optionIndex, optionValue, false);
      const isAvailable = optionHasVariant(optionIndex, optionValue);
      // Out of stock stays clickable so the customer can open the value and reach
      // the back-in-stock app; only a value with no variant behind it is disabled.
      const soldOut = hasVariant && !isAvailable;

      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-pressed', String(isSelected));
      button.disabled = !hasVariant;
      button.toggleAttribute('data-sold-out', soldOut);

      // The greyed pill is CSS only; without this the value reads as any other one.
      const status = button.querySelector('[data-quick-add-option-status]');
      if (status) status.textContent = soldOut ? dialog.dataset.soldOutLabel || '' : '';
    });
  };

  const updateVariant = (variant, updateImage = true) => {
    activeVariant = variant || null;
    const isAvailable = Boolean(activeVariant?.available);
    variantInput.value = activeVariant?.id ? String(activeVariant.id) : '';
    submitButton.disabled = !isAvailable;
    if (isAvailable) delete submitButton.dataset.variantUnavailable;
    else submitButton.dataset.variantUnavailable = 'true';
    submitLabel.textContent = activeVariant
      ? (isAvailable ? dialog.dataset.addLabel : dialog.dataset.soldOutLabel)
      : dialog.dataset.unavailableLabel;

    // Out of stock takes the CTA and the stepper out of the row rather than leaving
    // them disabled, and hands the width to whatever the app renders in the slot.
    submitButton.hidden = !isAvailable;
    if (quantityWrapper) quantityWrapper.hidden = !isAvailable;
    if (soldOutSlot) soldOutSlot.hidden = isAvailable;
    purchaseRow?.toggleAttribute('data-sold-out', !isAvailable);

    const verdict = cartRuleVerdict();
    const ruleMaximum = verdict && verdict.allowed > 0 ? verdict.allowed : Number.POSITIVE_INFINITY;
    const maximum = Math.min(variantMaximum(activeVariant), ruleMaximum);
    if (Number.isFinite(maximum)) quantityInput.max = String(maximum);
    else quantityInput.removeAttribute('max');

    const blocked = Boolean(isAvailable && verdict && verdict.allowed <= 0);
    if (blocked) {
      submitButton.disabled = true;
      submitLabel.textContent = verdict.status === 'incompatible'
        ? window.CartRules.labels.incompatible
        : window.CartRules.labels.maximum;
    }

    // Marks the state for CSS: lets the wide blocked label wrap instead of
    // squeezing the stepper, and keeps the disabled-button spinner off.
    submitButton.toggleAttribute('data-rule-blocked', blocked);

    // The disabled button only names the problem; the notice explains it.
    if (ruleNotice) {
      ruleNotice.textContent = blocked ? window.CartRules.message(verdict) : '';
      ruleNotice.hidden = !blocked;
    }

    clampQuantity();
    updateOptionStates();

    if (updateImage) setMainImage(variantImage(activeVariant) || activeImages[0] || '');
  };

  const renderOptions = () => {
    optionsContainer.replaceChildren();
    const definitions = visibleOptionDefinitions();
    optionsContainer.hidden = definitions.length === 0;

    definitions.forEach((definition) => {
      const fieldset = document.createElement('fieldset');
      fieldset.className = 'quick-add-modal__option';
      if (isFormatOption(definition)) {
        fieldset.classList.add('quick-add-modal__option--compact');
      }

      const legend = document.createElement('legend');
      legend.textContent = `${definition.name} :`;

      const values = document.createElement('div');
      values.className = 'quick-add-modal__option-values';
      definition.values.forEach((value) => {
        const button = document.createElement('button');
        button.className = 'quick-add-modal__option-value';
        button.type = 'button';
        const status = document.createElement('span');
        status.className = 'visually-hidden';
        status.dataset.quickAddOptionStatus = '';
        button.append(value, status);
        button.dataset.quickAddOptionValue = '';
        button.dataset.optionIndex = String(definition.index);
        button.dataset.optionValue = value;
        button.setAttribute('aria-pressed', 'false');
        values.append(button);
      });

      fieldset.append(legend, values);
      optionsContainer.append(fieldset);
    });
  };

  const writeProductData = () => {
    const definitions = visibleOptionDefinitions();
    const cartData = {
      moneyFormat: dialog.dataset.moneyFormat,
      vendor: activeProduct.vendor,
      title: activeProduct.title,
      optionNames: activeProduct.optionDefinitions.map((definition) => definition.name),
      hasOnlyDefaultVariant: definitions.length === 0,
      variants: activeProduct.variants.map((variant) => ({
        id: variant.id,
        available: variant.available,
        options: variant.options,
        priceAmount: Number(variant.price) || 0,
        compareAtPriceAmount: Number(variant.compare_at_price) || 0,
        imageUrl: imageWithWidth(variantImage(variant) || activeImages[0] || '', 160) || null,
      })),
    };
    productDataElement.textContent = JSON.stringify(cartData).replace(/</g, '\\u003c');
  };

  const renderProduct = (product, trigger) => {
    activeProduct = product;
    activeImages = product.images;
    activeVariant = product.variants.find((variant) => variant.available) || product.variants[0] || null;
    selectedOptions = activeVariant ? [...activeVariant.options] : [];
    activeProductUrl = trigger.dataset.productUrl;

    panel.dataset.productUrl = activeProductUrl;
    panel.setAttribute('data-product-root', '');
    const promoPrice = price.closest('[data-promo-price]');
    if (promoPrice) {
      promoPrice.dataset.promoProtein = String(
        Array.isArray(product.tags) && product.tags.some((tag) => String(tag).toLowerCase() === 'protein')
      );
    }
    form.quickAddOpener = trigger;
    // The dialog is shared between every card, so carry the triggering card's
    // cart rules (custom.max_quantity / custom.produits_incompatibles) over to
    // the form cart.js submits.
    copyCartRules(trigger, form);
    quantityInput.value = '1';
    renderTitle(product.title);

    const badgeText = trigger.dataset.productBadge || '';
    badge.textContent = badgeText;
    badge.hidden = !badgeText;

    renderGallery();
    renderOptions();
    writeProductData();
    updateVariant(activeVariant);

    loading.hidden = true;
    error.hidden = true;
    content.hidden = false;
    dialog.setAttribute('aria-busy', 'false');
    window.requestAnimationFrame(syncMainViewportPosition);
  };

  const showLoading = () => {
    dialog.setAttribute('aria-busy', 'true');
    loading.hidden = false;
    error.hidden = true;
    content.hidden = true;
  };

  const showError = () => {
    loading.hidden = true;
    content.hidden = true;
    error.hidden = false;
    dialog.setAttribute('aria-busy', 'false');
    dialog.querySelector('[data-quick-add-retry]')?.focus({ preventScroll: true });
  };

  const loadProduct = (trigger) => {
    const version = ++requestVersion;
    showLoading();
    fetchProduct(trigger.dataset.productUrl)
      .then((product) => {
        if (version !== requestVersion || !dialog.open) return;
        renderProduct(product, trigger);
      })
      .catch(() => {
        if (version === requestVersion && dialog.open) showError();
      });
  };

  const finishClose = (restoreFocus) => {
    window.clearTimeout(closeTimer);
    closeTimer = 0;
    dialog.classList.remove('is-closing');
    if (dialog.open) dialog.close();
    document.documentElement.classList.remove('has-open-quick-add');
    if (restoreFocus && lastTrigger?.isConnected) {
      window.requestAnimationFrame(() => lastTrigger?.focus({ preventScroll: true }));
    }
  };

  const closeDialog = ({ restoreFocus = true, immediate = false } = {}) => {
    if (!dialog.open || closeTimer) return;
    if (immediate || reducedMotion.matches) {
      finishClose(restoreFocus);
      return;
    }

    dialog.classList.add('is-closing');
    closeTimer = window.setTimeout(() => finishClose(restoreFocus), 170);
  };

  const openDialog = (trigger) => {
    const productUrl = trigger.dataset.productUrl;
    if (!productUrl) return;

    window.clearTimeout(prefetchTimer);
    prefetchTimer = 0;
    lastTrigger = trigger;
    activeProductUrl = productUrl;
    form.quickAddOpener = trigger;

    // Points-only flag/cost passed from the product card (metafields aren't in
    // /product.js), used by updatePrices to show points instead of money.
    fpoPointsOnly = trigger.dataset.fpoPointsOnly === 'true';
    fpoPointsCost = parseInt(trigger.dataset.fpoPointsCost, 10) || 0;

    if (!dialog.open) {
      document.documentElement.classList.add('has-open-quick-add');
      dialog.showModal();
      window.requestAnimationFrame(() => closeButton?.focus({ preventScroll: true }));
    }
    loadProduct(trigger);
  };

  // Points-only products can't be added for money from the quick-add modal —
  // intercept the submit (capture phase, before the theme's own handler) and send
  // the buyer to the product page, where the "buy with points" flow lives.
  form.addEventListener(
    'submit',
    (event) => {
      if (fpoPointsOnly && activeProductUrl) {
        event.preventDefault();
        event.stopImmediatePropagation();
        window.location.href = activeProductUrl;
      }
    },
    true,
  );

  const chooseOption = (button) => {
    const optionIndex = Number(button.dataset.optionIndex);
    const optionValue = button.dataset.optionValue;
    if (button.disabled || !Number.isInteger(optionIndex) || !optionValue) return;

    const candidateOptions = [...selectedOptions];
    candidateOptions[optionIndex] = optionValue;
    const exactVariant = findExactVariant(candidateOptions);
    const isFormat = isFormatOption(activeProduct.optionDefinitions[optionIndex]);

    // What can be bought wins: a value still purchasable under another option is
    // resolved to that combination. Only when nothing under this value is in stock
    // does the sold-out variant itself get selected, so the customer lands on it
    // and reaches the back-in-stock app instead of the click doing nothing.
    const nextVariant = (exactVariant?.available ? exactVariant : null)
      || (isFormat ? bestVariantFor(optionIndex, optionValue) : null)
      || exactVariant
      || (isFormat ? bestVariantFor(optionIndex, optionValue, false) : null);
    if (!nextVariant) return;

    selectedOptions = [...nextVariant.options];
    updateVariant(nextVariant);
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-quick-add-trigger]');
    if (!trigger) return;
    event.preventDefault();
    openDialog(trigger);
  });

  document.addEventListener('mouseover', (event) => {
    const trigger = event.target.closest('[data-quick-add-trigger]');
    if (!trigger || trigger.contains(event.relatedTarget)) return;
    window.clearTimeout(prefetchTimer);
    prefetchTimer = window.setTimeout(() => {
      prefetchTimer = 0;
      fetchProduct(trigger.dataset.productUrl).catch(() => {});
    }, 120);
  }, { passive: true });

  document.addEventListener('mouseout', (event) => {
    const trigger = event.target.closest('[data-quick-add-trigger]');
    if (!trigger || trigger.contains(event.relatedTarget)) return;
    window.clearTimeout(prefetchTimer);
    prefetchTimer = 0;
  }, { passive: true });

  document.addEventListener('focusin', (event) => {
    const trigger = event.target.closest('[data-quick-add-trigger]');
    if (trigger?.dataset.productUrl) fetchProduct(trigger.dataset.productUrl).catch(() => {});
  });

  thumbnails.addEventListener('scroll', scheduleThumbnailScrollbar, { passive: true });
  thumbnailRail?.addEventListener('wheel', queueThumbnailWheel, { passive: false });
  desktopGallery.addEventListener('change', scheduleThumbnailScrollbar);
  mainImageViewport.addEventListener('scroll', scheduleMainImageFromScroll, { passive: true });
  mobileGallery.addEventListener('change', () => {
    window.requestAnimationFrame(syncMainViewportPosition);
  });

  thumbnails.addEventListener('pointerdown', (event) => {
    if (
      !desktopGallery.matches
      || event.pointerType !== 'mouse'
      || event.button !== 0
      || thumbnails.scrollHeight - thumbnails.clientHeight <= 1
    ) return;

    thumbnailPanPointer = event.pointerId;
    thumbnailPanStartY = event.clientY;
    thumbnailPanStartScroll = thumbnails.scrollTop;
    thumbnailPanTargetScroll = thumbnails.scrollTop;
    thumbnailPanMoved = false;
    thumbnails.setPointerCapture(event.pointerId);
  });

  thumbnails.addEventListener('pointermove', (event) => {
    if (thumbnailPanPointer !== event.pointerId) return;

    const distance = event.clientY - thumbnailPanStartY;
    if (!thumbnailPanMoved && Math.abs(distance) < 4) return;

    event.preventDefault();
    thumbnailPanMoved = true;
    thumbnails.classList.add('is-dragging');
    thumbnailPanTargetScroll = thumbnailPanStartScroll - distance;
    queueThumbnailPan();
  });

  thumbnails.addEventListener('pointerup', finishThumbnailPan);
  thumbnails.addEventListener('pointercancel', finishThumbnailPan);
  thumbnails.addEventListener('lostpointercapture', finishThumbnailPan);
  thumbnails.addEventListener('click', (event) => {
    if (!suppressThumbnailClick) return;
    event.preventDefault();
    event.stopPropagation();
    suppressThumbnailClick = false;
  }, true);

  thumbnailScrollbar?.addEventListener('pointerdown', (event) => {
    if (event.button !== 0 || thumbnailScrollbar.hidden || !thumbnailScrollbarThumb) return;
    event.preventDefault();

    const thumbRect = thumbnailScrollbarThumb.getBoundingClientRect();
    const pressedOnThumb = event.clientY >= thumbRect.top && event.clientY <= thumbRect.bottom;
    thumbnailDragPointer = event.pointerId;
    thumbnailDragOffset = pressedOnThumb ? event.clientY - thumbRect.top : thumbRect.height / 2;
    thumbnailScrollbar.setPointerCapture(event.pointerId);
    scrollThumbnailsFromPointer(event.clientY);
  });

  thumbnailScrollbar?.addEventListener('pointermove', (event) => {
    if (thumbnailDragPointer !== event.pointerId) return;
    event.preventDefault();
    scrollThumbnailsFromPointer(event.clientY);
  });

  const finishThumbnailDrag = (event) => {
    if (!thumbnailScrollbar || thumbnailDragPointer !== event.pointerId) return;
    if (thumbnailScrollbar.hasPointerCapture(event.pointerId)) {
      thumbnailScrollbar.releasePointerCapture(event.pointerId);
    }
    thumbnailDragPointer = null;
  };

  thumbnailScrollbar?.addEventListener('pointerup', finishThumbnailDrag);
  thumbnailScrollbar?.addEventListener('pointercancel', finishThumbnailDrag);

  if ('ResizeObserver' in window) {
    thumbnailResizeObserver = new ResizeObserver(scheduleThumbnailScrollbar);
    thumbnailResizeObserver.observe(thumbnails);
  }

  dialog.addEventListener('click', (event) => {
    if (event.target === dialog || event.target.closest('[data-quick-add-close]')) {
      closeDialog();
      return;
    }

    if (event.target.closest('[data-quick-add-retry]')) {
      if (lastTrigger) loadProduct(lastTrigger);
      return;
    }

    const thumbnail = event.target.closest('[data-quick-add-thumbnail]');
    if (thumbnail) {
      setMainImage(thumbnail.dataset.imageUrl);
      return;
    }

    const optionValue = event.target.closest('[data-quick-add-option-value]');
    if (optionValue) {
      chooseOption(optionValue);
      return;
    }

    if (event.target.closest('[data-quick-add-minus]')) {
      clampQuantity(-1);
      return;
    }

    if (event.target.closest('[data-quick-add-plus]')) clampQuantity(1);
  });

  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    closeDialog();
  });

  dialog.addEventListener('close', () => {
    window.clearTimeout(mainViewportTargetTimer);
    mainViewportTargetIndex = null;
    if (mainViewportScrollFrame) window.cancelAnimationFrame(mainViewportScrollFrame);
    mainViewportScrollFrame = 0;
    document.documentElement.classList.remove('has-open-quick-add');
    dialog.classList.remove('is-closing');
  });

  // The drawer can change the cart while the dialog is open (add, then reopen).
  document.addEventListener('cart:rules-changed', () => {
    if (dialog.open && activeProduct) updateVariant(activeVariant, false);
  });

  quantityInput.addEventListener('change', () => clampQuantity());
  quantityInput.addEventListener('input', () => {
    const value = Number.parseInt(quantityInput.value, 10);
    minusButton.disabled = !Number.isFinite(value) || value <= 1;
    updatePrices();
  });

  form.addEventListener('submit', (event) => {
    if (!activeVariant?.available || !variantInput.value) {
      event.preventDefault();
      return;
    }

    clampQuantity();
    form.quickAddOpener = lastTrigger;
    closeDialog({ restoreFocus: false });
  });
})();
